import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ApiError } from "@atlas/shared";
import { ArrowLeft, Check, RefreshCw } from "lucide-react";
import {
  deleteGroup,
  fetchGroup,
  leaveGroup,
  regenerateInviteCode,
  reportGroup,
  setGroupMute,
  updateGroup,
  updateGroupMember,
  type GroupDetailResponse,
  type GroupMember,
  type GroupMute,
  type GroupRole,
} from "../../api/groups";
import type { UpdateGroupMemberInput } from "@atlas/shared";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { usePageMeta } from "../../hooks/usePageMeta";
import { GroupNotFound, inviteLink } from "./GroupPage";
import {
  ErrorLine,
  GroupImagePicker,
  GroupsPageFrame,
  INPUT_STYLE,
  LABEL_STYLE,
  MemberIdentity,
  groupErrorMessage,
  memberName,
} from "./group-ui";

// /groups/:id/settings (spec 32 / ATL-151): mute, leave, report; admins edit
// details, change the invite code and manage members; the owner can hand
// over ownership and delete the group. The API enforces every rule; this
// page only hides what the caller's role can't do.

const MUTE_OPTIONS: Array<{ value: GroupMute; label: string; hint: string }> = [
  {
    value: "none",
    label: "Everything",
    hint: "Pings for new posts, when someone overtakes you, and challenge starts, last days and results.",
  },
  {
    value: "posts",
    label: "Mute posts",
    hint: "No pings for new posts. Overtakes and challenge news still arrive.",
  },
  {
    value: "all",
    label: "Mute the group",
    hint: "No pings from this group at all. The board, challenges and feed are still here when you look.",
  },
];

function MutePanel({ data }: { data: GroupDetailResponse }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (mute: GroupMute) => setGroupMute(data.group.id, mute),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups", data.group.id] }),
  });
  const current = mutation.isPending && mutation.variables ? mutation.variables : data.me.mute;

  return (
    <AtlasPanel title="Notifications">
      <div role="radiogroup" aria-label="Group notifications" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {MUTE_OPTIONS.map((o) => {
          const active = current === o.value;
          return (
            <label
              key={o.value}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                borderRadius: "var(--radius-md)",
                border: `1px solid ${active ? "var(--border-strong)" : "var(--border-subtle)"}`,
                background: active ? "var(--surface-raised)" : "transparent",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="group-mute"
                checked={active}
                onChange={() => mutation.mutate(o.value)}
                style={{ marginTop: 3, accentColor: "var(--accent)" }}
              />
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>{o.label}</span>
                <span style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>{o.hint}</span>
              </span>
            </label>
          );
        })}
        {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "Couldn't save that. Try again.")}</ErrorLine>}
      </div>
    </AtlasPanel>
  );
}

function DetailsPanel({ data }: { data: GroupDetailResponse }) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(data.group.name);
  const [description, setDescription] = React.useState(data.group.description ?? "");
  const [imageUrl, setImageUrl] = React.useState<string | null>(data.group.imageUrl);
  const [uploading, setUploading] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      updateGroup(data.group.id, { name: name.trim(), description: description.trim() || null, imageUrl }),
    onSuccess: async () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const dirty =
    name.trim() !== data.group.name ||
    (description.trim() || null) !== (data.group.description ?? null) ||
    imageUrl !== data.group.imageUrl;

  return (
    <AtlasPanel title="Details">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && name.trim()) mutation.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={LABEL_STYLE}>Image</span>
          <GroupImagePicker name={name} value={imageUrl} onChange={setImageUrl} onBusy={setUploading} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="settings-name" style={LABEL_STYLE}>Name</label>
          <input id="settings-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} style={INPUT_STYLE} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="settings-description" style={LABEL_STYLE}>Description</label>
          <textarea
            id="settings-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={280}
            rows={3}
            style={{ ...INPUT_STYLE, height: "auto", padding: 10, resize: "vertical" }}
          />
        </div>
        {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "Couldn't save. Try again.")}</ErrorLine>}
        <div>
          <Button type="submit" variant="secondary" size="sm" disabled={!dirty || !name.trim() || uploading || mutation.isPending} leftIcon={saved ? <Check size={13} /> : undefined}>
            {mutation.isPending ? "Saving…" : saved ? "Saved" : "Save"}
          </Button>
        </div>
      </form>
    </AtlasPanel>
  );
}

function InvitePanel({ data }: { data: GroupDetailResponse }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const mutation = useMutation({
    mutationFn: () => regenerateInviteCode(data.group.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups", data.group.id] }),
  });

  function handleRegenerate() {
    if (window.confirm("Make a new invite code? The current link stops working straight away, including links already shared.")) {
      mutation.mutate();
    }
  }

  return (
    <AtlasPanel title="Invite code">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <code
          className="tnum"
          style={{
            display: "block",
            padding: "8px 10px",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            fontFamily: "var(--font-mono)",
            fontSize: 12.5,
            color: "var(--text-secondary)",
            overflowWrap: "anywhere",
          }}
        >
          {inviteLink(data.group.inviteCode, user?.username)}
        </code>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.45 }}>
          If a link ends up somewhere it shouldn't, make a new code. Members already in the group stay in.
        </p>
        {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "Couldn't make a new code.")}</ErrorLine>}
        <div>
          <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={13} />} disabled={mutation.isPending} onClick={handleRegenerate}>
            {mutation.isPending ? "Making a new code…" : "New invite code"}
          </Button>
        </div>
      </div>
    </AtlasPanel>
  );
}

function roleLabel(role: GroupRole): string {
  return role === "owner" ? "Owner" : role === "admin" ? "Admin" : "Member";
}

function MemberRow({ member, data, myUserId }: { member: GroupMember; data: GroupDetailResponse; myUserId: string | undefined }) {
  const queryClient = useQueryClient();
  const myRole = data.me.role;
  const isSelf = member.userId === myUserId;
  const mutation = useMutation({
    mutationFn: (input: UpdateGroupMemberInput) => updateGroupMember(data.group.id, member.userId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups"] }),
  });

  // Owners act on anyone but themselves; admins on plain members only.
  const canAct = !isSelf && member.role !== "owner" && (myRole === "owner" || (myRole === "admin" && member.role === "member"));
  const name = memberName(member);

  function confirmThen(message: string, input: UpdateGroupMemberInput) {
    if (window.confirm(message)) mutation.mutate(input);
  }

  return (
    <li style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 0", borderBottom: "1px solid var(--border-subtle)", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <MemberIdentity member={member} subtitle={<span className="eyebrow" style={{ fontSize: 9.5 }}>{roleLabel(member.role)}{isSelf ? " · you" : ""}</span>} />
        </div>
        {canAct && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {member.role === "member" && (
              <Button variant="ghost" size="sm" disabled={mutation.isPending} onClick={() => mutation.mutate({ role: "admin" })}>
                Make admin
              </Button>
            )}
            {member.role === "admin" && myRole === "owner" && (
              <Button variant="ghost" size="sm" disabled={mutation.isPending} onClick={() => mutation.mutate({ role: "member" })}>
                Remove admin
              </Button>
            )}
            {myRole === "owner" && (
              <Button
                variant="ghost"
                size="sm"
                disabled={mutation.isPending}
                onClick={() =>
                  confirmThen(`Hand ownership to ${name}? You become an admin, and only they can delete the group.`, { role: "owner" })
                }
              >
                Make owner
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => confirmThen(`Remove ${name} from the group? They can rejoin with an invite link.`, { status: "removed" })}
            >
              Remove
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => confirmThen(`Ban ${name}? They're removed and can't rejoin, even with a new link.`, { status: "banned" })}
            >
              Ban
            </Button>
          </div>
        )}
      </div>
      {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "That didn't work. Try again.")}</ErrorLine>}
    </li>
  );
}

function MembersPanel({ data }: { data: GroupDetailResponse }) {
  const { user } = useAuth();
  return (
    <AtlasPanel title={`Members · ${data.group.memberCount.toLocaleString("en-GB")}`}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {data.members.map((m) => (
          <MemberRow key={m.userId} member={m} data={data} myUserId={user?.id} />
        ))}
      </ul>
    </AtlasPanel>
  );
}

function ReportPanel({ groupId }: { groupId: string }) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const mutation = useMutation({ mutationFn: () => reportGroup(groupId, reason.trim()) });

  return (
    <AtlasPanel title="Report this group">
      {mutation.isSuccess ? (
        <p style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--spruce-300)", margin: 0 }}>
          <Check size={13} /> Reported. Thanks, we'll take a look.
        </p>
      ) : !open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.45 }}>
            For a name, description or image that breaks the community guidelines. To report a post, use the flag on the post.
          </p>
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>Report group</Button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What's wrong with this group?"
            maxLength={500}
            rows={3}
            style={{ ...INPUT_STYLE, height: "auto", padding: 10, resize: "vertical" }}
          />
          {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "Couldn't send the report.")}</ErrorLine>}
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" size="sm" disabled={!reason.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? "Sending…" : "Send report"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </AtlasPanel>
  );
}

function LeaveAndDeletePanel({ data }: { data: GroupDetailResponse }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isOwner = data.me.role === "owner";
  const othersRemain = data.group.memberCount > 1;

  // Navigate away first: invalidating while this page is mounted would
  // refetch the group just left and flash its 404 state.
  const afterExit = () => {
    void navigate({ to: "/groups" }).then(() => {
      queryClient.removeQueries({ queryKey: ["groups", data.group.id] });
      queryClient.removeQueries({ queryKey: ["social/feed", "group", data.group.id] });
      void queryClient.invalidateQueries({ queryKey: ["groups"] });
    });
  };

  const leaveMutation = useMutation({ mutationFn: () => leaveGroup(data.group.id), onSuccess: afterExit });
  const deleteMutation = useMutation({ mutationFn: () => deleteGroup(data.group.id), onSuccess: afterExit });

  function handleLeave() {
    const last = !othersRemain;
    const message = last
      ? "You're the last member. Leaving deletes the group. Your own collections are not touched."
      : "Leave this group? Your collections are not touched, and you can rejoin with an invite link.";
    if (window.confirm(message)) leaveMutation.mutate();
  }

  function handleDelete() {
    const typed = window.prompt(`Delete "${data.group.name}" for everyone? Type the group name to confirm.`);
    if (typed != null && typed.trim() === data.group.name.trim()) deleteMutation.mutate();
  }

  const ownerBlocked = isOwner && othersRemain;

  return (
    <AtlasPanel title="Leave">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.45 }}>
          {ownerBlocked
            ? "You own this group. Hand ownership to another member under Members before you leave."
            : "Leaving never touches your collections or activities."}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button variant="secondary" size="sm" disabled={ownerBlocked || leaveMutation.isPending} onClick={handleLeave}>
            {leaveMutation.isPending ? "Leaving…" : "Leave group"}
          </Button>
          {isOwner && (
            <Button variant="danger" size="sm" disabled={deleteMutation.isPending} onClick={handleDelete}>
              {deleteMutation.isPending ? "Deleting…" : "Delete group"}
            </Button>
          )}
        </div>
        {isOwner && (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.45 }}>
            Deleting removes the board, challenges and group posts for everyone. Nobody loses anything they collected.
          </p>
        )}
        {leaveMutation.isError && <ErrorLine>{groupErrorMessage(leaveMutation.error, "Couldn't leave the group.")}</ErrorLine>}
        {deleteMutation.isError && <ErrorLine>{groupErrorMessage(deleteMutation.error, "Couldn't delete the group.")}</ErrorLine>}
      </div>
    </AtlasPanel>
  );
}

export default function GroupSettingsPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const groupQuery = useQuery({
    queryKey: ["groups", id],
    queryFn: () => fetchGroup(id),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  usePageMeta({
    title: "Group settings - Atlas",
    description: "Settings for a private Atlas group.",
    canonicalPath: "/groups",
    noindex: true,
  });

  if (groupQuery.isLoading) {
    return (
      <GroupsPageFrame>
        <Skel height="60px" />
        <Skel height="240px" radius="var(--radius-lg)" />
      </GroupsPageFrame>
    );
  }
  if (groupQuery.isError || !groupQuery.data) return <GroupNotFound error={groupQuery.error} />;

  const data = groupQuery.data;
  const isAdmin = data.me.role === "owner" || data.me.role === "admin";

  return (
    <GroupsPageFrame>
      <Link
        to="/groups/$id"
        params={{ id }}
        className="eyebrow"
        style={{ fontSize: 10, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}
      >
        <ArrowLeft size={10} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data.group.name}</span>
      </Link>
      <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>Group settings</h1>

      <div
        className="r-cols-1"
        style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 20, alignItems: "start" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          <MutePanel data={data} />
          {isAdmin && <DetailsPanel data={data} />}
          {isAdmin && <InvitePanel data={data} />}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          {isAdmin && <MembersPanel data={data} />}
          <LeaveAndDeletePanel data={data} />
          <ReportPanel groupId={data.group.id} />
        </div>
      </div>
    </GroupsPageFrame>
  );
}
